<template>
  <div aria-valuemax="100" aria-valuemin="0" :aria-valuenow="typeof value === 'number' ? value : undefined" class="v-progress" role="progressbar">
    <!-- @slot Customize the content above the progress bar. -->
    <div v-if="label || $slots.default" class="v-progress__label">
      <slot>
        <div v-if="label" v-text="label" />
      </slot>
    </div>

    <div class="v-progress-bar" :style="{ height: `${height}px`, width }">
      <VLoadingBar v-show="loading" :size="height" />
      <div v-show="!loading" class="v-progress-bar__track" :class="Array.isArray(value) ? 'v-progress-bar__track--multiple' : undefined" :style="{ backgroundColor: trackColor }">
        <div v-if="typeof value === 'number'" class="v-progress-bar__thumb" :class="type" :style="{ transform: transform(), backgroundColor: color }" />
        <div
          v-for="(progress, index) in value"
          v-else
          :key="index"
          class="v-progress-bar__thumb"
          :class="progress.type ?? type"
          :style="{ transform: transform(index), backgroundColor: progress.color, zIndex: value.length - index }"
        />
      </div>

      <div v-if="marker !== undefined" class="v-progress-bar__marker v-progress-bar__marker-top" :class="type" :style="{ left: `${marker}%`, transform: 'translateX(-50%)' }" />
      <div v-if="marker !== undefined" class="v-progress-bar__marker v-progress-bar__marker-bottom" :class="type" :style="{ left: `${marker}%`, transform: 'translateX(-50%)' }" />
    </div>

    <div v-if="footer || $slots.footer" class="v-progress__footer">
      <slot name="footer">
        <div v-if="footer" v-text="footer" />
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect } from 'vue'

import type { VProgressType } from '../types.ts'
import { nextFrame } from '../utils/helpers.ts'
import VLoadingBar from './VLoadingBar.vue'

interface Progress {
  value: number
  color?: string
  type?: VProgressType
}

interface Props {
  value: Progress[] | number
  loading?: boolean
  animateOnMount?: boolean
  type?: VProgressType
  height?: number
  width?: string
  label?: string
  footer?: string
  color?: string
  trackColor?: string
  marker?: number
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  animateOnMount: true,
  type: 'primary',
  height: 8,
  width: '100%',
  label: '',
  footer: '',
  color: undefined,
  trackColor: '#e0e0e0',
  marker: undefined,
})

defineSlots<{
  default?(): any
  footer?(): any
}>()

const animated = ref(false)
const valueBeforeLoading = ref<null | number | Progress[]>(null)

watchEffect(async () => {
  if (!props.loading) {
    await nextFrame()
    await nextFrame()
    valueBeforeLoading.value = null
  } else {
    valueBeforeLoading.value = props.value
  }
})

onMounted(async () => {
  await nextFrame()
  await nextFrame()
  animated.value = true
})

function getValueFromArray(index: number, array: Progress[]) {
  let value = 0

  for (const currentIndex in array) {
    value += array[currentIndex].value

    if (Number(currentIndex) === index) {
      break
    }
  }

  return value
}

function transform(index = null as number | null) {
  if (props.animateOnMount && !animated.value) {
    return 'translateX(-100%)'
  }

  if (index === null) {
    return transformValue(props.value as number, valueBeforeLoading.value as number)
  }

  return transformValueInArray(index)
}

function transformValue(value: number, valueBeforeLoading: number | null) {
  if (valueBeforeLoading !== null) {
    return `translateX(-${100 - valueBeforeLoading}%)`
  }

  return `translateX(-${100 - value}%)`
}

function transformValueInArray(index: number) {
  let value = getValueFromArray(index, props.value as Progress[])
  if (value > 100) value = 100

  return transformValue(value, valueBeforeLoading.value ? getValueFromArray(index, valueBeforeLoading.value as Progress[]) : null)
}

defineExpose({
  valueBeforeLoading,
  transform,
})
</script>

<style>
.v-progress {
  width: 100%;

  .v-progress__label {
    margin-bottom: 2px;
    line-height: 16px;
    font-size: 16px;
    display: flex;
    align-items: center;
  }

  .v-progress__footer {
    font-size: 11px;
    line-height: 9px;
    margin-top: 4px;
  }

  .v-progress-bar {
    width: 100%;
    position: relative;
  }

  .v-progress-bar__track {
    width: 100%;
    height: 100%;
    border-radius: 9999px;
    overflow: hidden;
    position: relative;

    &.v-progress-bar__track {
      display: flex;

      .v-progress-bar__thumb {
        position: absolute;
        border-radius: 0;

        &:last-child {
          border-radius: 9999px;
        }
      }
    }

    .v-progress-bar__thumb {
      width: 100%;
      height: 100%;
      border-radius: 9999px;
      transition: transform 1s ease;

      &.primary {
        background-color: var(--color-primary);
      }

      &.secondary {
        background-color: var(--color-secondary);
      }

      &.red {
        background-color: var(--color-red);
      }

      &.orange {
        background-color: var(--color-orange);
      }

      &.green {
        background-color: var(--color-green);
      }

      &.green-light {
        background-color: var(--color-green-light);
      }

      &.yellow {
        background-color: var(--color-yellow);
      }

      &.blue-5 {
        background-color: var(--color-blue-5);
      }

      &.dark {
        background-color: var(--color-grey-40);
      }

      &.white {
        background-color: #fff;
      }
    }
  }
}

.v-progress-bar__marker {
  position: absolute;
  border: 4px solid transparent;
  z-index: 2;
}

.v-progress-bar__marker-top {
  top: -4px;

  .primary {
    border-top-color: var(--color-primary);
  }

  .secondary {
    border-top-color: var(--color-secondary);
  }

  .red {
    border-top-color: var(--color-red);
  }

  .orange {
    border-top-color: var(--color-orange);
  }

  .green {
    border-top-color: var(--color-green);
  }

  .green-light {
    border-top-color: var(--color-green-light);
  }

  .yellow {
    border-top-color: var(--color-yellow);
  }

  .blue-5 {
    border-top-color: var(--color-blue-5);
  }

  .dark {
    border-top-color: var(--color-grey-40);
  }

  .white {
    border-top-color: #fff;
  }
}

.v-progress-bar__marker-bottom {
  bottom: -4px;

  .primary {
    border-bottom-color: var(--color-primary);
  }

  .secondary {
    border-bottom-color: var(--color-secondary);
  }

  .red {
    border-bottom-color: var(--color-red);
  }

  .orange {
    border-bottom-color: var(--color-orange);
  }

  .green {
    border-bottom-color: var(--color-green);
  }

  .green-light {
    border-bottom-color: var(--color-green-light);
  }

  .yellow {
    border-bottom-color: var(--color-yellow);
  }

  .blue-5 {
    border-bottom-color: var(--color-blue-5);
  }

  .dark {
    border-bottom-color: var(--color-grey-40);
  }

  .white {
    border-bottom-color: #fff;
  }
}
</style>
